import React, { useEffect, useState } from 'react';
import { Card, Table, Tag, Space, Typography } from 'antd';
import { ColumnsType } from 'antd/es/table';
import { VIP_STATUS_LABELS, VIP_STATUS_COLORS, VipStatus } from '../types';
import apiClient from '../services/api';

const { Title } = Typography;

// Interface for active user ranking data
interface ActiveUserRanking {
  rank: number;
  user_id: string;
  user_name: string;
  email: string;
  vip_status: number;
  clinic: string;
  prefecture: string;
  created_at: string;
  active_days: number;
  total_minutes: number;
  avg_minutes_per_day: number;
}

interface ActiveUsersRankingResponse {
  rankings: ActiveUserRanking[];
  period: string;
  total_users: number;
}

const ActiveUsersRanking: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<ActiveUserRanking[]>([]);

  useEffect(() => {
    fetchActiveUsersRanking();
  }, []);

  const fetchActiveUsersRanking = async () => {
    try {
      setLoading(true);
      const response: ActiveUsersRankingResponse = await apiClient.get('/api/analytics/active-users-ranking');
      console.log('Active users ranking response:', response);
      setData(response.rankings);
    } catch (error) {
      console.error('Failed to fetch active users ranking:', error);
    } finally {
      setLoading(false);
    }
  };

  const getRankDisplay = (rank: number): React.ReactNode => {
    switch (rank) {
      case 1:
        return <span>🥇 {rank}</span>;
      case 2:
        return <span>🥈 {rank}</span>;
      case 3:
        return <span>🥉 {rank}</span>;
      default:
        return rank;
    }
  };

  const columns: ColumnsType<ActiveUserRanking> = [
    {
      title: 'ランク',
      dataIndex: 'rank',
      key: 'rank',
      width: 80,
      align: 'center',
      render: (rank: number) => getRankDisplay(rank),
    },
    {
      title: 'ユーザー名',
      dataIndex: 'user_name',
      key: 'user_name',
      width: 200,
      render: (name: string, record: ActiveUserRanking) => (
        <div>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '8px',
            marginBottom: '4px'
          }}>
            <span style={name === '退会済み' ? { color: 'red' } : {}}>
              {name}
            </span>
            <Tag 
              color={VIP_STATUS_COLORS[record.vip_status as VipStatus]}
              style={{ fontSize: '11px' }}
            >
              {VIP_STATUS_LABELS[record.vip_status as VipStatus]}
            </Tag>
          </div>
          <div style={{ fontSize: '11px', color: '#666' }}>
            {record.email || 'メールアドレス不明'}
          </div>
        </div>
      ),
    },
    {
      title: 'クリニック',
      dataIndex: 'clinic',
      key: 'clinic',
      width: 200,
      ellipsis: true,
      render: (clinic: string, record: ActiveUserRanking) => (
        <span 
          style={{ 
            color: '#1890ff', 
            cursor: 'pointer',
            textDecoration: 'underline'
          }}
          onClick={() => {
            if (clinic && clinic !== 'なし') {
              const searchQuery = record.prefecture ? 
                `${clinic} ${record.prefecture}` : 
                clinic;
              const googleSearchUrl = `https://www.google.com/search?q=${encodeURIComponent(searchQuery)}`;
              window.open(googleSearchUrl, '_blank');
            }
          }}
        >
          {clinic || 'なし'}
        </span>
      ),
    },
    {
      title: '都道府県',
      dataIndex: 'prefecture',
      key: 'prefecture',
      width: 100,
    },
    {
      title: '登録日',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 100,
      render: (date: string) => date || '不明',
    },
    {
      title: 'アクティブ日数',
      dataIndex: 'active_days',
      key: 'active_days',
      width: 120,
      align: 'center',
      render: (days: number) => `${days}日`,
    },
    {
      title: '平均利用時間',
      dataIndex: 'avg_minutes_per_day',
      key: 'avg_minutes_per_day',
      width: 120,
      align: 'right',
      render: (minutes: any) => `${Number(minutes).toFixed(1)}分/日`,
    },
  ];

  return (
    <Card 
      title={
        <Space>
          <Title level={4} style={{ margin: 0 }}>アクティブユーザーランキング</Title>
          <span style={{ color: '#999', fontSize: '14px' }}>Active User Ranking</span>
        </Space>
      }
    >
      <Table
        loading={loading}
        columns={columns}
        dataSource={data}
        rowKey="user_id"
        pagination={false}
        scroll={{ x: 1000 }}
      />
    </Card>
  );
};

export default ActiveUsersRanking;